import React, { useCallback, useMemo, useEffect, useState } from 'react';
import Session from './common/Session.types';
import { UserInfo } from './common/interfaces/UserInfo.types';
import { AuthContext, EMPTY_AUTH_STATE, EMPTY_USER_INFO } from './common/authContext';
import { AuthState } from './common/AuthContext.types';
import useStateWithSessionStorage from './common/sessionStorageState';
import {
  MFASetupContext,
  EMPTY_MFA_SETUP_CREDENTIALS,
  MFASetupCredentials
} from './common/mfaSetupContext';
import './styles/core.scss';
import RouterConfig from './routing/RouterConfig';
import Layout from './components/Layout/Layout';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import './utils/chartjs/setupChartjs';
import { RoutingHistoryContext, useSaveLandingURL } from './common/routingHistoryContext';
import analytics from './utils/analytics/analytics';
import useGoogleAnalytics from './hooks/useGoogleAnalytics';

const App = () => {
  const [authState, setAuthState] = useStateWithSessionStorage<AuthState>(
    'authState',
    EMPTY_AUTH_STATE
  );
  const [session, setSession] = useStateWithSessionStorage('userSession', Session.LoggedOut);
  const [userInfo, setUserInfo] = useStateWithSessionStorage<UserInfo>('userRole', EMPTY_USER_INFO);

  // MFA setup credentials are deliberately held in memory only (NOT in
  // sessionStorage) so they are never written to disk, URLs, or any
  // other persistent surface. They are cleared on logout / unmount.
  const [mfaSetupCredentials, setMFASetupCredentials] = useState<MFASetupCredentials>(
    EMPTY_MFA_SETUP_CREDENTIALS
  );

  const clearMFASetupCredentials = useCallback(
    () => setMFASetupCredentials(EMPTY_MFA_SETUP_CREDENTIALS),
    []
  );

  const mfaSetupContext = useMemo(
    () => ({
      credentials: mfaSetupCredentials,
      setCredentials: setMFASetupCredentials,
      clearCredentials: clearMFASetupCredentials
    }),
    [mfaSetupCredentials, clearMFASetupCredentials]
  );

  const authContext = useMemo(() => {
    const resetAuthState = () => {
      setAuthState(EMPTY_AUTH_STATE);
    };

    return {
      authState,
      setAuthState,
      resetAuthState,
      userInfo,
      setUserInfo,
      session,
      setSession
    };
  }, [authState, session, setAuthState, setSession, setUserInfo, userInfo]);

  const routingHistory = useSaveLandingURL();
  useGoogleAnalytics();

  useEffect(() => {
    if (session !== Session.LoggedOut) {
      analytics.sendLoginEvent(userInfo);
    }
  }, [session, userInfo]);

  return (
    <AuthContext.Provider value={authContext}>
      <MFASetupContext.Provider value={mfaSetupContext}>
        <RoutingHistoryContext.Provider value={routingHistory}>
          <Layout>
            <ErrorBoundary>
              <RouterConfig />
            </ErrorBoundary>
          </Layout>
        </RoutingHistoryContext.Provider>
      </MFASetupContext.Provider>
    </AuthContext.Provider>
  );
};

export default App;
